/**
 * Partnership Contract Onboarding Flow
 *
 * Multi-step wizard for creating and signing partnership contracts during onboarding.
 *
 * Steps:
 * 1. Partner Information
 * 2. Financial Terms
 * 3. Contract Review
 * 4. E-Signature
 * 5. Completion
 */
import React, { useState } from 'react';
import {
  Box,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  TextField,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
  Card,
  CardContent,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { contractService } from '../../services/contractService';

interface OnboardingData {
  partnerEmail: string;
  partnerPhone: string;
  initialCapital: number;
  useTemplate: boolean;
  customProfitSplit?: {
    cp: number;
    op: number;
    reinvest: number;
  };
  customMaxStake?: number;
  targetMonthlyRevenue?: number;
  enableWeeklyCheckins: boolean;
}

const steps = [
  'Partner Information',
  'Financial Terms',
  'Review Contract',
  'Sign Contract',
  'Complete'
];

const PartnershipContractOnboarding: React.FC = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contractId, setContractId] = useState<string | null>(null);
  const [contractPreview, setContractPreview] = useState<any>(null);

  // Form data
  const [onboardingData, setOnboardingData] = useState<OnboardingData>({
    partnerEmail: '',
    partnerPhone: '',
    initialCapital: 1000000,
    useTemplate: true,
    customMaxStake: 40,
    targetMonthlyRevenue: 500000,
    enableWeeklyCheckins: true
  });

  // Signature state
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [agreedToESignature, setAgreedToESignature] = useState(false);
  const [signatureType, setSignatureType] = useState<'typed' | 'drawn'>('typed');
  const [typedName, setTypedName] = useState('');
  const [signatureData, setSignatureData] = useState<string | null>(null);

  const handleNext = async () => {
    setError(null);

    // Validate current step
    if (activeStep === 0) {
      if (!onboardingData.partnerEmail) {
        setError('Partner email is required');
        return;
      }
    }

    if (activeStep === 1) {
      if (onboardingData.initialCapital <= 0) {
        setError('Initial capital must be greater than 0');
        return;
      }

      // Create contract
      setLoading(true);
      try {
        const contract = await contractService.quickSetup(onboardingData);
        setContractId(contract.id);

        // Load preview
        const preview = await contractService.getPreview(contract.id);
        setContractPreview(preview);
        setActiveStep(activeStep + 1);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to create contract');
      } finally {
        setLoading(false);
      }
      return;
    }

    if (activeStep === 2) {
      // Send for review
      if (!contractId) {
        setError('Contract not found');
        return;
      }

      setLoading(true);
      try {
        await contractService.sendForReview(contractId);
        await contractService.markReadyToSign(contractId);
        setActiveStep(activeStep + 1);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to send for review');
      } finally {
        setLoading(false);
      }
      return;
    }

    if (activeStep === 3) {
      // Sign contract
      if (!agreedToTerms || !agreedToESignature) {
        setError('You must agree to terms and e-signature');
        return;
      }

      if (signatureType === 'typed' && !typedName) {
        setError('Please enter your full name');
        return;
      }

      setLoading(true);
      try {
        await contractService.signContract(contractId!, {
          signature_method: signatureType === 'typed' ? 'TYPED' : 'DRAWN',
          signature_data: signatureType === 'typed' ? typedName : signatureData,
          agreed_to_terms: agreedToTerms,
          agreed_to_esignature: agreedToESignature
        });
        setActiveStep(activeStep + 1);
      } catch (err: any) {
        setError(err.response?.data?.detail || 'Failed to sign contract');
      } finally {
        setLoading(false);
      }
      return;
    }

    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
    setError(null);
  };

  const handleFieldChange = (field: keyof OnboardingData, value: any) => {
    setOnboardingData(prev => ({ ...prev, [field]: value }));
  };

  // STEP 1: Partner Information
  const renderPartnerInfoStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Who are you partnering with?
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Enter your partner's contact information. They'll receive an invitation to review and sign the contract.
      </Typography>

      <TextField
        fullWidth
        label="Partner Email"
        type="email"
        value={onboardingData.partnerEmail}
        onChange={(e) => handleFieldChange('partnerEmail', e.target.value)}
        margin="normal"
        required
        helperText="Your operating partner will receive contract invitation at this email"
      />

      <TextField
        fullWidth
        label="Partner Phone (Optional)"
        type="tel"
        value={onboardingData.partnerPhone}
        onChange={(e) => handleFieldChange('partnerPhone', e.target.value)}
        margin="normal"
        helperText="For SMS notifications"
      />
    </Box>
  );

  // STEP 2: Financial Terms
  const renderFinancialTermsStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Financial Terms
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Configure the investment amount and partnership structure.
      </Typography>

      <TextField
        fullWidth
        label="Initial Capital Investment"
        type="number"
        value={onboardingData.initialCapital}
        onChange={(e) => handleFieldChange('initialCapital', parseFloat(e.target.value))}
        margin="normal"
        required
        InputProps={{
          startAdornment: '₦'
        }}
        helperText="Amount you're investing into the partnership"
      />

      <FormControl component="fieldset" margin="normal">
        <FormLabel>Profit Sharing Model</FormLabel>
        <RadioGroup
          value={onboardingData.useTemplate ? 'default' : 'custom'}
          onChange={(e) => handleFieldChange('useTemplate', e.target.value === 'default')}
        >
          <FormControlLabel
            value="default"
            control={<Radio />}
            label="Default (33.33% / 33.33% / 33.33% - You/Partner/Reinvest)"
          />
          <FormControlLabel
            value="custom"
            control={<Radio />}
            label="Custom Split"
          />
        </RadioGroup>
      </FormControl>

      {!onboardingData.useTemplate && (
        <Box sx={{ ml: 4, mt: 2 }}>
          <TextField
            label="Your Share (%)"
            type="number"
            value={onboardingData.customProfitSplit?.cp || 33.33}
            onChange={(e) => handleFieldChange('customProfitSplit', {
              ...onboardingData.customProfitSplit,
              cp: parseFloat(e.target.value)
            })}
            sx={{ width: '30%', mr: 1 }}
          />
          <TextField
            label="Partner Share (%)"
            type="number"
            value={onboardingData.customProfitSplit?.op || 33.33}
            onChange={(e) => handleFieldChange('customProfitSplit', {
              ...onboardingData.customProfitSplit,
              op: parseFloat(e.target.value)
            })}
            sx={{ width: '30%', mr: 1 }}
          />
          <TextField
            label="Reinvestment (%)"
            type="number"
            value={onboardingData.customProfitSplit?.reinvest || 33.34}
            onChange={(e) => handleFieldChange('customProfitSplit', {
              ...onboardingData.customProfitSplit,
              reinvest: parseFloat(e.target.value)
            })}
            sx={{ width: '30%' }}
          />
        </Box>
      )}

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle1" gutterBottom>
        Progressive Equity
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Your partner starts at 0% ownership and earns up to this maximum as the business grows.
      </Typography>

      <TextField
        fullWidth
        label="Partner Maximum Ownership Stake (%)"
        type="number"
        value={onboardingData.customMaxStake}
        onChange={(e) => handleFieldChange('customMaxStake', parseFloat(e.target.value))}
        margin="normal"
        helperText="Typically 40%. You'll always maintain at least 60%."
        inputProps={{ min: 0, max: 50 }}
      />

      <Divider sx={{ my: 3 }} />

      <Typography variant="subtitle1" gutterBottom>
        Performance Targets (Optional)
      </Typography>

      <TextField
        fullWidth
        label="Target Monthly Revenue"
        type="number"
        value={onboardingData.targetMonthlyRevenue}
        onChange={(e) => handleFieldChange('targetMonthlyRevenue', parseFloat(e.target.value))}
        margin="normal"
        InputProps={{
          startAdornment: '₦'
        }}
        helperText="Optional revenue goal"
      />

      <FormControlLabel
        control={
          <Checkbox
            checked={onboardingData.enableWeeklyCheckins}
            onChange={(e) => handleFieldChange('enableWeeklyCheckins', e.target.checked)}
          />
        }
        label="Enable Weekly Check-ins (first 6 months)"
      />
    </Box>
  );

  // STEP 3: Review Contract
  const renderContractReviewStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review Partnership Contract
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Please review the contract terms carefully before proceeding to signature.
      </Typography>

      {contractPreview && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Contract Summary
            </Typography>
            <Box sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.875rem', mb: 3 }}>
              {contractPreview.contract_summary}
            </Box>

            <Typography variant="subtitle1" gutterBottom>
              Key Terms
            </Typography>
            <List dense>
              {Object.entries(contractPreview.key_terms).map(([key, value]) => (
                <ListItem key={key}>
                  <ListItemText
                    primary={key.replace(/_/g, ' ').toUpperCase()}
                    secondary={value as string}
                  />
                </ListItem>
              ))}
            </List>

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Next Steps:
              </Typography>
              <List dense>
                {contractPreview.requires_approval?.map((item: string, idx: number) => (
                  <ListItem key={idx}>
                    <ListItemText primary={`• ${item}`} />
                  </ListItem>
                ))}
              </List>
            </Alert>
          </CardContent>
        </Card>
      )}

      <Alert severity="warning" sx={{ mb: 2 }}>
        By proceeding, you confirm that you've read and understood all terms of this partnership agreement.
      </Alert>
    </Box>
  );

  // STEP 4: E-Signature
  const renderSignatureStep = () => (
    <Box>
      <Typography variant="h6" gutterBottom>
        Sign Partnership Contract
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Your electronic signature is legally binding and equivalent to a handwritten signature.
      </Typography>

      <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={agreedToTerms}
              onChange={(e) => setAgreedToTerms(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography variant="body2">
              I have read and agree to all terms and conditions in this Partnership Contract
            </Typography>
          }
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={agreedToESignature}
              onChange={(e) => setAgreedToESignature(e.target.checked)}
              color="primary"
            />
          }
          label={
            <Typography variant="body2">
              I agree that my electronic signature is legally binding and equivalent to my handwritten signature
            </Typography>
          }
        />
      </Card>

      <FormControl component="fieldset" sx={{ mb: 3 }}>
        <FormLabel>Signature Method</FormLabel>
        <RadioGroup
          value={signatureType}
          onChange={(e) => setSignatureType(e.target.value as 'typed' | 'drawn')}
        >
          <FormControlLabel value="typed" control={<Radio />} label="Type My Name" />
          <FormControlLabel value="drawn" control={<Radio />} label="Draw Signature" />
        </RadioGroup>
      </FormControl>

      {signatureType === 'typed' && (
        <TextField
          fullWidth
          label="Type Your Full Legal Name"
          value={typedName}
          onChange={(e) => setTypedName(e.target.value)}
          placeholder="John Doe"
          sx={{ mb: 3 }}
          helperText="This will serve as your electronic signature"
        />
      )}

      {signatureType === 'drawn' && (
        <Box>
          <Typography variant="body2" color="text.secondary" paragraph>
            Draw your signature in the box below:
          </Typography>
          <Box
            sx={{
              border: '2px solid #ccc',
              borderRadius: 1,
              height: 200,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 2,
              bgcolor: '#f9f9f9'
            }}
          >
            {/* TODO: Integrate signature canvas library */}
            <Typography color="text.secondary">
              Signature Canvas (Integrate react-signature-canvas)
            </Typography>
          </Box>
          <Button variant="outlined" size="small">
            Clear Signature
          </Button>
        </Box>
      )}

      <Alert severity="info" sx={{ mt: 3 }}>
        <Typography variant="caption">
          Your signature will be recorded with IP address, timestamp, and device information for legal compliance.
        </Typography>
      </Alert>
    </Box>
  );

  // STEP 5: Completion
  const renderCompletionStep = () => (
    <Box textAlign="center">
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: 80, color: 'success.main', lineHeight: 1 }}>✓</Typography>
      </Box>
      <Typography variant="h5" gutterBottom>
        Contract Signed Successfully! 🎉
      </Typography>
      <Typography variant="body1" color="text.secondary" paragraph>
        Your partnership contract has been signed and is now active.
      </Typography>

      <Alert severity="success" sx={{ my: 3, textAlign: 'left' }}>
        <Typography variant="subtitle2" gutterBottom>
          What Happens Next?
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText primary="• Your partner will receive a notification to review and sign" />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Once both parties sign, the contract becomes active" />
          </ListItem>
          <ListItem>
            <ListItemText primary="• You'll receive a PDF copy via email" />
          </ListItem>
          <ListItem>
            <ListItemText primary="• Initial capital transfer instructions will be sent" />
          </ListItem>
        </List>
      </Alert>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mt: 4 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate(`/partnerships/contracts/${contractId}`)}
        >
          View Contract
        </Button>
        <Button
          variant="outlined"
          onClick={() => navigate('/dashboard')}
        >
          Go to Dashboard
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', py: 4, px: 2 }}>
      <Typography variant="h4" gutterBottom align="center">
        Partnership Contract Setup
      </Typography>
      <Typography variant="body2" color="text.secondary" align="center" paragraph>
        Create and sign your profit-sharing partnership agreement in minutes
      </Typography>

      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ minHeight: 400 }}>
        {activeStep === 0 && renderPartnerInfoStep()}
        {activeStep === 1 && renderFinancialTermsStep()}
        {activeStep === 2 && renderContractReviewStep()}
        {activeStep === 3 && renderSignatureStep()}
        {activeStep === 4 && renderCompletionStep()}
      </Box>

      {activeStep < steps.length - 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
          <Button
            disabled={activeStep === 0 || loading}
            onClick={handleBack}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={loading}
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Processing...
              </>
            ) : (
              activeStep === steps.length - 2 ? 'Sign Contract' : 'Next'
            )}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default PartnershipContractOnboarding;
